import React from "react";
import { useFeatureFlag, FLAGS } from "../../../../featureFlags";
import DateField from "../DateField";
import EmphasizedMessage from "../EmphasizedMessage";
import PillSelect from "../PillSelect";
import { toDisplayDate } from "../services/timings";
import { STITCH_MODES } from "./constants";
import styles from "./StitchBatchSection.module.css";

/**
 * The Stitch batch section body (the consumer wraps it in a SectionCard).
 *
 * Two entry variants: the plain "Stitch batch" checkbox, or — when the course has
 * continuation courses — the auto-stitch checkbox naming them, whose chain the
 * backend creates in the same request (R-07-02). Unticking auto-stitch reveals the
 * manual options (R-07-01): link one of the student's own live batches (R-07-06),
 * or create a new successor for another of the student's courses (R-07-03,
 * Q-PR15). The successor starts on the backend's earliest start after this batch
 * ends; the start field may move it later, never earlier (Q-D3).
 *
 * @param {{ entry: {checked: boolean, onChange: (checked: boolean) => void,
 *             autoStitch?: {courseNames: string[], checked: boolean,
 *               onChange: (checked: boolean) => void}|null},
 *           mode: {value: "existing"|"new", onChange: (mode: string) => void,
 *             disabledValues?: string[]},
 *           target: {options: {value: string, label: string}[], value: string,
 *             onChange: (batchId: string) => void},
 *           course: {options: {value: string, label: string}[], value: string,
 *             onChange: (courseId: string) => void},
 *           sessions: {value?: number|string, onChange: (value: string) => void,
 *             max?: number|null, extra?: number},
 *           start?: {isShown: boolean, value: string, min: string, isPending?: boolean,
 *             isReadOnly?: boolean, error?: string, previewError?: string,
 *             onChange: (value: string) => void},
 *           summary: {batchName: string, batchEndsOn: string, note?: string},
 *           children?: React.ReactNode }} props
 */
const StitchBatchSection = ({ entry, mode, target, course, sessions, start, summary, children }) => {
  const { autoStitch } = entry ?? {};
  /* M2+M3 release flag (same gate name as the backend's): the T-14 wording below describes the
     cron-and-loop behaviour that only exists with the flag ON; OFF keeps the shipped
     chain-at-submission sentence so the screen never promises behaviour that is not live. */
  const autoStitchT14On = useFeatureFlag(FLAGS?.EPICENTER_AUTO_STITCH_T14?.name);
  const showFlow = autoStitch ? !autoStitch?.checked : entry?.checked;
  const isNew = mode?.value === "new";
  /* The backend's ceiling for this batch (R-07-03); nothing is said below it. */
  const isAboveMax =
    sessions?.max !== null && sessions?.max !== undefined && Number(sessions?.value) > sessions?.max;
  /* A mode with nothing behind it — no batch to link, no other course to create
     one for — is shown disabled rather than refusing the click silently. */
  const modeOptions = STITCH_MODES?.map((option) =>
    mode?.disabledValues?.includes(option?.value) ? { ...option, disabled: true } : option,
  );

  return (
    <div className={styles.wrap}>
      {autoStitch ? (
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={!!autoStitch?.checked}
            onChange={(event) => autoStitch?.onChange?.(event?.target?.checked)}
          />
          <span>
            {autoStitch?.courseNames?.map((name, index) => (
              <React.Fragment key={name}>
                {index > 0 && " and "}
                <strong>{name}</strong>
              </React.Fragment>
            ))}{" "}
            {autoStitch?.courseNames?.length > 1
              ? `are${autoStitchT14On ? " the" : ""} succeeding courses`
              : "is the succeeding course"}
            {autoStitchT14On ? (
              <>
                . The current batch ends on{" "}
                <strong>{summary?.batchEndsOn || "its computed end date"}</strong>. The system will
                initiate the process{" "}
                <strong>14 days before {summary?.batchEndsOn || "it ends"}</strong> by sending a
                Loop Request to the top 10 eligible mentors. The succeeding batch will then be
                created and stitched automatically.
                {/* keep "top 10" in lockstep with EpicenterApi's LOOP_NOTIFY_MAX */}
              </>
            ) : (
              <>, batches will be automatically created and stitched upon submission</>
            )}
          </span>
        </label>
      ) : (
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={!!entry?.checked}
            onChange={(event) => entry?.onChange?.(event?.target?.checked)}
          />
          Stitch batch
        </label>
      )}

      {showFlow && (
        <>
          <div className={isNew ? styles.panelNew : styles.panel}>
            <div className={styles.optionGroup}>
              <div className={styles.fieldLabel}>Select an option</div>
              <PillSelect options={modeOptions} value={mode?.value} onChange={mode?.onChange} />
            </div>

            {!isNew && (
              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Batch to stitch</span>
                  {target?.options?.length > 0 ? (
                    <select
                      className={styles.select}
                      value={target?.value ?? ""}
                      onChange={(event) => target?.onChange?.(event?.target?.value)}
                    >
                      <option value="" disabled>
                        Select a batch
                      </option>
                      {target?.options?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className={styles.readonlyField}>
                      This student has no batch that can be stitched after this one
                    </div>
                  )}
                </label>
              </div>
            )}

            {isNew && (
              <>
                <div className={styles.fieldGrid}>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Course</span>
                    {course?.options?.length === 0 ? (
                      <div className={styles.readonlyField}>
                        This student has no other course to create a batch for
                      </div>
                    ) : (
                      <select
                        className={styles.select}
                        value={course?.value ?? ""}
                        onChange={(event) => course?.onChange?.(event?.target?.value)}
                      >
                        <option value="" disabled>
                          Select a course
                        </option>
                        {course?.options?.map((option) => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Sessions</span>
                    <input
                      type="number"
                      className={styles.select}
                      min={1}
                      max={sessions?.max ?? undefined}
                      aria-invalid={isAboveMax}
                      value={sessions?.value ?? ""}
                      onChange={(event) => sessions?.onChange?.(event?.target?.value)}
                    />
                    {!isAboveMax && sessions?.extra > 0 && (
                      <span className={styles.fieldHint}>
                        Includes {sessions?.extra} extra {sessions?.extra === 1 ? "class" : "classes"} from the
                        purchase&apos;s credits, left after the batch above.
                      </span>
                    )}
                    {isAboveMax && (
                      <span className={styles.fieldError} role="alert">
                        The maximum available for this course is {sessions?.max} sessions.
                      </span>
                    )}
                  </label>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Batch name</span>
                  <div className={styles.readonlyField}>{summary?.batchName || "—"}</div>
                </div>
              </>
            )}

            {/* The line under the date runs the width of the panel, not the date's
                column, where it wrapped to three lines. */}
            {start?.isShown && (
              <div className={styles.field}>
                <div className={styles.fieldGrid}>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Start date of the stitched batch</span>
                    <DateField
                      value={start?.value}
                      min={start?.min}
                      disabled={start?.isReadOnly || !start?.min}
                      invalid={!!start?.error}
                      ariaLabel="Start date of the stitched batch"
                      onChange={start?.onChange}
                    />
                  </label>
                </div>
                {start?.error && (
                  <span className={styles.fieldError} role="alert">
                    <EmphasizedMessage text={start?.error} />
                  </span>
                )}
                {!start?.error && start?.min && !start?.isReadOnly && (
                  <span className={styles.fieldHint}>
                    Earliest <strong>{toDisplayDate(start?.min)}</strong>, the first class day after this
                    batch ends. Pick a later class day to start it later.
                  </span>
                )}
                {!start?.error && !start?.min && start?.isPending && (
                  <span className={styles.fieldHint}>Working out the earliest start…</span>
                )}
                {!start?.error && !start?.min && !start?.isPending && start?.previewError && (
                  <span className={styles.fieldError} role="alert">
                    <EmphasizedMessage text={start?.previewError} />
                  </span>
                )}
              </div>
            )}

            {summary?.batchEndsOn && (
              <div className={styles.infoStrip}>
                This batch ends on <strong>{summary?.batchEndsOn}</strong>. The stitched batch starts
                after it.
                {summary?.note && <> {summary?.note}</>}
              </div>
            )}
          </div>

          {isNew && children}
        </>
      )}
    </div>
  );
};

export default StitchBatchSection;
